import { Accordion } from '@uhg-abyss/web/ui/Accordion';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import find from 'lodash/find';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import {
  Constants,
  EAP_OPTION_LABEL,
  FilterCollectionModel,
  FilterOptionsType,
  ReverseCoverageTypesCodes,
} from '../../../../common/Constants';
import { getFeatureFlag } from '../../../../common/Utils';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import {
  Option,
  ProviderFiltersType,
  ProviderSearchFilters,
} from '../../../../models/ProviderSearch';
import { useChipStore } from '../../../../store/useChipStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { returnSuppressFlag } from '../../../../utils/featureSuppress';
import {
  getLagoonConfigValue,
  parseLagoonConfigArray,
} from '../../../../utils/providerSearch.utils';
import { adobeLinkTrackEvent } from '../../../AdobeTagging/adobeLinkTrackEvent';
import { CheckBoxGroup } from '../../../CheckBoxGroup';
import { ConstantsLagoon } from '../../../ConstantsLagoon';
import { isPrioritySchedulingActive } from '../../../ContentFilters/BccFilterUtils';
import { Markdown } from '../../../Markdown';
import { RadioGroup } from '../../../RadioGroup';
import { FilterDropDown } from '../../FilterDropdown';
import { FilterSlider } from '../../FilterSlider';
import { AllFilterSkeleton } from './AllFilterSkeleton';
import { GroupedAoeFilter } from './GroupedAoeFilter';

type Props = {
  providerFilters: ProviderFiltersType[];
  selectedFilters: ProviderSearchFilters;
  onFilterSelectionChange: (
    data: ProviderSearchFilters,
    lastModifiedModel: string
  ) => void;
  value?: string[];
  sectionType: string | undefined;
  AccordionType?: string;
  isData: boolean;
};

type OptionTypeProps = {
  isDisabled?: boolean;
  options: Option[];
  model: string;
  label: string;
  type: string;
};

export const ProviderFilters = ({
  providerFilters,
  selectedFilters,
  onFilterSelectionChange,
  value,
  sectionType,
  AccordionType = 'single',
  isData,
}: Props) => {
  const { t } = useTranslation();
  const { coverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
    }))
  );
  const [
    enableEapFilterUsingBHProgramFF,
    enableVirtualCareFilterAnalytics,
    enhanceDistanceFilter,
    enableBhGroupedAoeFilter,
    enableNetworkAffiliationFlow,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_EAP_FILTER_USING_BH_PROGRAM,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_VIRTUAL_CARE_FILTER_ANALYTICS_V2,
    ConstantsLagoon.FEATURE_FLAGS.ENHANCE_DISTANCE_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_GROUPED_AOE_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NETWORK_AFFILIATION_FLOW,
  ]);

  const isBhCoverage =
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] ||
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL CARVE-OUT'];

  if (enableEapFilterUsingBHProgramFF) {
    providerFilters = providerFilters.map((filter) => {
      if (filter.model === FilterCollectionModel.AREA_OF_EXPERTISE) {
        const updatedOptions = filter.options.filter(
          (option) => option?.dnt_label !== EAP_OPTION_LABEL
        );
        return { ...filter, options: updatedOptions };
      }
      return filter;
    });
  }

  const data = useLagoon('ui-messaging')();
  const UpcomingAvailabilityFilterContent = find(data, {
    key: ConstantsLagoon.CONTENT_FILTERS
      .UPCOMING_AVAILABILITY_FILTER_DESCRIPTION,
  });

  const NetworkAffiliationsFilterContent = find(data, {
    key: ConstantsLagoon.CONTENT_FILTERS
      .NETWORK_AFFILIATIONS_FILTER_INFO_MESSAGE,
  });

  const shouldApplyBhGroupedAoeOrder = enableBhGroupedAoeFilter && isBhCoverage;

  const getBhGroupedAoePriority = (model: string): number => {
    if (model === FilterCollectionModel.DISTANCE) {
      return 0;
    }
    if (model === FilterCollectionModel.UPCOMING_AVAILABILITY) {
      return 1;
    }
    if (model === FilterCollectionModel.VIRTUAL_CARE_OPTIONS) {
      return 2;
    }
    if (model === FilterCollectionModel.AREA_OF_EXPERTISE) {
      return 3;
    }
    return Number.MAX_SAFE_INTEGER;
  };
  const allConfigs = useLagoon('config');
  const pediatricSpecialtyCodesRaw = getLagoonConfigValue(
    allConfigs,
    ConstantsLagoon.CONFIG.PEDIATRIC_SPECIALTY_CODES
  );
  const pediatricSpecialtyCodes = parseLagoonConfigArray(
    pediatricSpecialtyCodesRaw
  );

  const isPediatricToggleActive =
    selectedFilters['LicensedInPediatrics']?.value;

  const [openedAccordion, setOpenedAccordion] = useState<string>('');
  const [hideSkeleton, setHideSkeleton] = useState(false);
  const allFiltersDrawer: string = 'all filters drawer';

  const isVirtualCareFiltersSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS
      .VIRTUAL_CARE_UNET_FILTER_SUPPRESSION
  );

  const isVirtualCareUnetRailroadSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_RAILROAD
  );

  const suppressVirtualCare =
    isVirtualCareFiltersSuppressed ||
    (coverageType !== ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] &&
      isVirtualCareUnetRailroadSuppressed);

  const isPrioritySchedulingEnabled =
    isPrioritySchedulingActive(selectedFilters);

  const getLocationForAnalytics = (label: string) =>
    `modal:${allFiltersDrawer}:${label} filter`;

  const onOptionChange = (e: { [key: string]: Option[] }) => {
    const currentSelectedFilters = {
      ...selectedFilters,
      ...e,
    };
    const firstValue = Object.values(e)[0];
    const lastModifiedModel =
      Array.isArray(firstValue) && firstValue.length > 0
        ? Object.keys(e)[0]
        : '';
    onFilterSelectionChange(currentSelectedFilters, lastModifiedModel);
  };

  function onChangeAccordion(e) {
    let linkName;
    let linkType;
    const linkLocation: string = `modal:${allFiltersDrawer}`;

    if (e && openedAccordion !== e) {
      linkName = providerFilters?.find((option) => e === option.model)?.label;
      linkType = 'accordion open';
    } else {
      linkName = providerFilters?.find(
        (option) => openedAccordion === option.model
      )?.label;
      linkType = 'accordion close';
    }

    const selectedModel = providerFilters?.find(
      (option) => e === option.model
    )?.model;

    if (
      selectedModel !== FilterCollectionModel.VIRTUAL_CARE_OPTIONS ||
      enableVirtualCareFilterAnalytics
    ) {
      adobeLinkTrackEvent({
        name: linkName,
        location: linkLocation,
        type: linkType,
      });
    }

    setOpenedAccordion(e);
  }
  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();
  const isSedationPerformed: any = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.SEDATION_DENTISTRY_SERVICES_PERFORMED
  );

  useEffect(() => {
    setHideSkeleton(isData);
  }, []);

  const renderResultBasedOnType = ({
    isDisabled,
    options,
    model,
    label,
    type,
  }: OptionTypeProps) => {
    let filteredOptions: any = options;

    // Filter for Services provided
    if (label == 'Services provided') {
      filteredOptions = isSedationPerformed
        ? options
        : options.filter(
            (option) =>
              option.label !== FilterCollectionModel.SEDATION_DENTISTRY
          );
    }

    // Filter for Specialty when Licensed in Pediatrics is active
    if (
      model === FilterCollectionModel.SPECIALTY_CODE &&
      isPediatricToggleActive
    ) {
      filteredOptions = options.filter((option) =>
        pediatricSpecialtyCodes.includes(option.value)
      );
    }

    if (
      model === FilterCollectionModel.UPCOMING_AVAILABILITY &&
      isPrioritySchedulingEnabled
    ) {
      const hasSevenDayOption = filteredOptions?.some(
        (option) => String(option.value) === '7'
      );

      if (!hasSevenDayOption) {
        filteredOptions = [
          ...filteredOptions,
          {
            label: 'Next 7 days',
            value: '7',
            dnt_label: 'Next 7 days',
          },
        ];
      }
    }

    // Render grouped AOE filter for Behavioral Health when the feature flag is on
    if (
      model === FilterCollectionModel.AREA_OF_EXPERTISE &&
      enableBhGroupedAoeFilter &&
      isBhCoverage
    ) {
      return (
        <GroupedAoeFilter
          defaultValue={
            Array.isArray(selectedFilters[model])
              ? selectedFilters[model].map((item: Option) => String(item.value))
              : []
          }
          isDisabled={isDisabled}
          locationForAnalytics={getLocationForAnalytics(label)}
          model={model}
          onOptionChange={onOptionChange}
          options={filteredOptions}
        />
      );
    }

    switch (type) {
      case FilterOptionsType.RADIO:
        return (
          <RadioGroup
            defaultValue={
              selectedFilters[model]?.length
                ? selectedFilters[model][0].value
                : ''
            }
            isDisabled={isDisabled}
            locationForAnalytics={getLocationForAnalytics(label)}
            model={model}
            onOptionChange={onOptionChange}
            options={filteredOptions}
          />
        );
      case FilterOptionsType.SLIDER:
        return (
          <FilterSlider
            defaultValue={
              selectedFilters[model]
                ? selectedFilters[model].map((item: Option) => item.value)?.[0]
                : 0
            }
            isDisabled={isDisabled}
            locationForAnalytics={getLocationForAnalytics(label)}
            model={model}
            onOptionChange={onOptionChange}
            options={filteredOptions}
          />
        );
      case FilterOptionsType.DROPDOWN:
        return (
          <FilterDropDown
            defaultValue={
              selectedFilters[model]?.length
                ? selectedFilters[model][0].value
                : ''
            }
            isDisabled={isDisabled}
            locationForAnalytics={getLocationForAnalytics(label)}
            model={model}
            onOptionChange={onOptionChange}
            options={filteredOptions}
            selectInputLabel={label}
          />
        );
      default:
        return (
          <>
            {/*
              Removing TextInput, and its code logic bacause WW with Chiranjib have a feature
              from Mallory that is using similar logic built before here, and applying
              the search box to all filters
            */}
            <CheckBoxGroup
              defaultValue={
                Array.isArray(selectedFilters[model])
                  ? selectedFilters[model].map((item: Option) => item.value)
                  : []
              }
              isDisabled={isDisabled}
              locationForAnalytics={getLocationForAnalytics(label)}
              model={model}
              onOptionChange={onOptionChange}
              options={filteredOptions}
            />
          </>
        );
    }
  };

  return (
    <Accordion
      data-testid="show-result"
      defaultValue={value}
      isCollapsible
      key={value?.join('-')}
      onValueChange={(e) => onChangeAccordion(e)}
      type={AccordionType}
    >
      {hideSkeleton
        ? providerFilters
            ?.filter(({ allFilterDrawerFilters }) =>
              allFilterDrawerFilters[coverageType]?.includes(sectionType)
            )
            .filter((filter) => {
              if (
                filter.model !==
                FilterCollectionModel.PLATINUM_PROVIDER_INDICATOR
              ) {
                if (
                  filter.model !== FilterCollectionModel.VIRTUAL_CARE_OPTIONS ||
                  !suppressVirtualCare
                ) {
                  return filter;
                }

                return null;
              }

              return null;
            })
            .sort((a, b) => {
              if (shouldApplyBhGroupedAoeOrder) {
                const priorityDiff =
                  getBhGroupedAoePriority(a.model) -
                  getBhGroupedAoePriority(b.model);
                if (priorityDiff !== 0) {
                  return priorityDiff;
                }
              }

              return (
                a.allFilterDrawerOrder[coverageType] -
                b.allFilterDrawerOrder[coverageType]
              );
            })
            .map(({ isDisabled, options, model, label, title, type }) => {
              {
                model === FilterCollectionModel.NETWORK_AFFILIATIONS &&
                  enableNetworkAffiliationFlow &&
                  NetworkAffiliationsFilterContent?.message && (
                    <span data-testid="network-affiliations-filter-description">
                      <Markdown>
                        {NetworkAffiliationsFilterContent.message}
                      </Markdown>
                    </span>
                  );
              }
              const isPrioritySchedulingDisabledSection =
                isPrioritySchedulingEnabled &&
                (model === FilterCollectionModel.VIRTUAL_CARE_OPTIONS ||
                  model === FilterCollectionModel.UPCOMING_AVAILABILITY ||
                  model === FilterCollectionModel.EAP_OFFERED);

              const effectiveIsDisabled =
                isDisabled || isPrioritySchedulingDisabledSection;

              const shouldShowPrioritySchedulingTooltip =
                effectiveIsDisabled && isPrioritySchedulingDisabledSection;

              const getAccordionTitle = () => {
                if (enableBhGroupedAoeFilter && isBhCoverage) {
                  if (model === FilterCollectionModel.SPECIALTY_CODE) {
                    return t('CONTENT_FILTERS.LICENSE_AND_EDUCATION');
                  }
                  if (model === FilterCollectionModel.VIRTUAL_CARE_OPTIONS) {
                    return t('CONTENT_FILTERS.VIRTUAL_AND_IN_PERSON_CARE');
                  }
                }
                return t(title || label);
              };

              const filterTrigger = (
                <Accordion.Header
                  data-testid={`${label
                    .replace(/ /g, '-')
                    .toLowerCase()}-filter-heading`}
                >
                  <Flex css={{ gap: '8px' }}>{getAccordionTitle()}</Flex>
                </Accordion.Header>
              );

              const filterContent = (
                <Accordion.Content>
                  {label === 'Upcoming availability' && (
                    <span
                      data-testid={`${label
                        .replace(/ /g, '-')
                        .toLowerCase()}-filter-description`}
                    >
                      <Markdown>
                        {UpcomingAvailabilityFilterContent?.message ?? ''}
                      </Markdown>
                    </span>
                  )}
                  {renderResultBasedOnType({
                    isDisabled: effectiveIsDisabled,
                    options,
                    model,
                    label,
                    type,
                  })}
                </Accordion.Content>
              );

              return enhanceDistanceFilter &&
                model === FilterCollectionModel.DISTANCE ? (
                <Accordion.Item
                  data-testid={`${label
                    .replace(/ /g, '-')
                    .toLowerCase()}-filter`}
                  key={model}
                  value={model}
                >
                  {renderResultBasedOnType({
                    isDisabled,
                    options,
                    model,
                    label,
                    type,
                  })}
                </Accordion.Item>
              ) : (
                <Accordion.Item
                  data-testid={`${label
                    .replace(/ /g, '-')
                    .toLowerCase()}-filter`}
                  key={model}
                  value={model}
                >
                  {shouldShowPrioritySchedulingTooltip ? (
                    <Tooltip
                      content={String(
                        t(
                          'CONTENT_FILTERS.PRIORITY_SCHEDULING_DISABLED_TOOLTIP'
                        ) || ''
                      )}
                    >
                      <div
                        data-testid={`${label
                          .replace(/ /g, '-')
                          .toLowerCase()}-priority-scheduling-disabled-tooltip`}
                      >
                        {filterTrigger}
                        {filterContent}
                      </div>
                    </Tooltip>
                  ) : (
                    <>
                      {filterTrigger}
                      {filterContent}
                    </>
                  )}
                </Accordion.Item>
              );
            })
        : [1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
            <AllFilterSkeleton index={i} key={i} />
          ))}
    </Accordion>
  );
};
